import type { AdminBillingPlanSettingsInput, BillingPlanId } from "@shudian/contracts";

export interface EntitlementOption {
  value: string;
  label: string;
  description: string;
}

export const ENTITLEMENT_OPTIONS: EntitlementOption[] = [
  { value: "store:read", label: "查看店铺", description: "读取账号下的店铺、店铺配置和运营视图" },
  { value: "store:create", label: "创建店铺", description: "允许桌面端新增店铺资料" },
  { value: "store:write_credential", label: "维护店铺账号", description: "保存平台账号等店铺凭据" },
  { value: "store:write_proxy", label: "维护代理环境", description: "保存店铺代理配置" },
  { value: "store:write_publish_config", label: "维护上架配置", description: "保存露天上架默认配置" },
  { value: "store:write_cookie", label: "保存登录态", description: "保存店铺浏览器 Cookie 和登录快照" },
  { value: "store:open_browser", label: "店铺浏览器", description: "打开独立店铺浏览器环境" },
  { value: "product:collect", label: "商品采集", description: "允许采集和整理商品资料" },
  { value: "listing:publish", label: "批量上架", description: "允许露天批量发布刊登" },
  { value: "listing:unpublish", label: "下架刊登", description: "允许执行下架动作" },
  { value: "price:update", label: "修改价格", description: "允许批量改价动作" },
  { value: "inventory:update", label: "修改库存", description: "允许批量改库存动作" },
  { value: "sync:use", label: "云同步与换机", description: "允许同步 root_key、店铺敏感资料和浏览器资料" }
];

export const DEFAULT_OPERATION_ENTITLEMENTS = ENTITLEMENT_OPTIONS.map((option) => option.value);

type PlanTemplate = Pick<
  AdminBillingPlanSettingsInput,
  | "name"
  | "description"
  | "priceAmount"
  | "quotaLimits"
  | "entitlements"
  | "deviceLimit"
  | "storeLimit"
  | "features"
  | "renewalHint"
  | "sortOrder"
>;

const PLAN_TEMPLATES: Record<BillingPlanId, PlanTemplate> = {
  shudian_monthly: {
    name: "月套餐",
    description: "适合刚开始运营的单月开通方案",
    priceAmount: 1280,
    quotaLimits: [],
    entitlements: DEFAULT_OPERATION_ENTITLEMENTS,
    deviceLimit: 3,
    storeLimit: 50,
    features: ["50 个店铺额度", "露天商品采集与整理", "批量上架前人工确认", "云同步与换机恢复"],
    renewalHint: "按月续费，支付成功后立即延长 1 个月",
    sortOrder: 10
  },
  shudian_quarterly: {
    name: "季度套餐",
    description: "适合稳定运营的季度开通方案",
    priceAmount: 3480,
    quotaLimits: [],
    entitlements: DEFAULT_OPERATION_ENTITLEMENTS,
    deviceLimit: 3,
    storeLimit: 150,
    features: ["150 个店铺额度", "多店铺浏览器环境", "露天批量上架", "规则检查与风险提示"],
    renewalHint: "按季度续费，支付成功后立即延长 3 个月",
    sortOrder: 20
  },
  shudian_yearly: {
    name: "年套餐",
    description: "适合长期运营和多店铺规模化账号",
    priceAmount: 12800,
    quotaLimits: [],
    entitlements: DEFAULT_OPERATION_ENTITLEMENTS,
    deviceLimit: 5,
    storeLimit: 500,
    features: ["500 个店铺额度", "长期云同步与换机恢复", "商品整理与 Listing 检查", "批量上架和运营动作追踪"],
    renewalHint: "按年续费，支付成功后立即延长 1 年",
    sortOrder: 30
  }
};

export function billingPlanTemplate(planId: BillingPlanId): AdminBillingPlanSettingsInput {
  const template = PLAN_TEMPLATES[planId];
  return {
    planId,
    name: template.name,
    description: template.description,
    priceAmount: template.priceAmount,
    quotaLimits: template.quotaLimits.map((quota) => ({ ...quota })),
    entitlements: [...template.entitlements],
    deviceLimit: template.deviceLimit,
    storeLimit: template.storeLimit,
    active: true,
    features: [...template.features],
    renewalHint: template.renewalHint,
    sortOrder: template.sortOrder
  };
}

export function formatEntitlementLabel(value: string): string {
  return ENTITLEMENT_OPTIONS.find((option) => option.value === value)?.label ?? value;
}
